    <app-page-shell>
      <app-page-hero eyebrow="HR Core" title="Employees" subtitle="Tenant-scoped employee records for office, driver, dispatch, warehouse, fleet, and site teams.">
      </app-page-hero>

      <app-page-data-panel title="Filters" subtitle="Search employee master records by logistics role, status, and identity-link readiness." [resultText]="totalRecords + ' records'">
        <div class="filter-grid">
          <p-inputGroup class="search-field">
            <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input id="hrEmployeeSearch" pInputText [(ngModel)]="filters.searchText" (keyup.enter)="load()" />
              <label for="hrEmployeeSearch">Search name, number, email, phone</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrWorkerCategory" [options]="categoryOptions" [(ngModel)]="filters.workerCategory" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrWorkerCategory">Worker category</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-circle-check"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrEmployeeStatus" [options]="statusOptions" [(ngModel)]="filters.status" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrEmployeeStatus">Status</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-building-user"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrDepartmentFilter" [options]="departmentOptions" [(ngModel)]="filters.departmentId" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrDepartmentFilter">Department</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrPositionFilter" [options]="positionOptions" [(ngModel)]="filters.positionId" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrPositionFilter">Position</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrDriverLinkFilter" [options]="linkOptions" [(ngModel)]="filters.driverLinked" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrDriverLinkFilter">Driver link</label>
            </p-iftaLabel>
          </p-inputGroup>
          <p-inputGroup>
            <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="hrPortalLinkFilter" [options]="linkOptions" [(ngModel)]="filters.customerLinked" [showClear]="true" styleClass="w-full"></p-select>
              <label for="hrPortalLinkFilter">Portal link</label>
            </p-iftaLabel>
          </p-inputGroup>
          <button pButton type="button" label="Search" icon="pi pi-search" (click)="load()"></button>
          <button pButton type="button" label="Clear" icon="pi pi-filter-slash" severity="secondary" [outlined]="true" (click)="clearFilters()"></button>
        </div>
      </app-page-data-panel>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-data-panel title="Employee Master" subtitle="Records are real HR employees only; legacy portal users appear through migration proposals until approved.">
        @if (canCreateEmployee) {
          <div actions><button pButton type="button" label="New Employee" icon="pi pi-plus" routerLink="/hr/employees/add"></button></div>
        }
        <p-table
          [value]="employees"
          [loading]="loading"
          [lazy]="true"
          [paginator]="true"
          [rows]="filters.pageSize || 50"
          [first]="firstRow"
          [totalRecords]="totalRecords"
          [rowsPerPageOptions]="[25, 50, 100]"
          styleClass="p-datatable-sm"
          responsiveLayout="scroll"
          [tableStyle]="{ 'min-width': '1080px' }"
          (onPage)="onPage($event)">
          <ng-template pTemplate="header">
            <tr>
              <th>Employee</th>
              <th>Category</th>
              <th>Status</th>
              <th>Organization</th>
              <th>Links</th>
              <th>Contact</th>
              <th style="width: 10rem">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-employee>
            <tr>
              <td>
                <div class="main-cell">
                  <strong>{{ employee.displayName }}</strong>
                  <span>{{ employee.employeeNumber }}</span>
                </div>
              </td>
              <td><p-tag [value]="format(employee.workerCategory)" severity="info"></p-tag></td>
              <td><p-tag [value]="format(employee.status)" [severity]="statusSeverity(employee.status)"></p-tag></td>
              <td>
                <div class="muted-stack">
                  <span>{{ employee.departmentName || 'No department' }}</span>
                  <small>{{ employee.positionName || employee.jobName || 'No position' }}</small>
                </div>
              </td>
              <td>
                <div class="chip-row">
                  <p-tag [value]="employee.customerLinked ? 'Portal linked' : 'No portal'" [severity]="employee.customerLinked ? 'success' : 'secondary'"></p-tag>
                  <p-tag [value]="employee.driverLinked ? 'Driver linked' : 'No driver'" [severity]="employee.driverLinked ? 'success' : 'secondary'"></p-tag>
                </div>
              </td>
              <td>
                <div class="muted-stack">
                  <span>{{ employee.emailAddress || '-' }}</span>
                  <small>{{ employee.phoneNumber || '-' }}</small>
                </div>
              </td>
              <td>
                <div class="actions">
                  <button pButton type="button" icon="pi pi-eye" [text]="true" [rounded]="true" [routerLink]="['/hr/employees', employee.employeeId]"></button>
                  @if (canEditEmployee) {
                    <button pButton type="button" icon="pi pi-pencil" [text]="true" [rounded]="true" [routerLink]="['/hr/employees', employee.employeeId, 'edit']"></button>
                  }
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="7">No HR employees found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>
